import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { UNPROCESSABLE_REBALANCING_ERROR_CODE } from '@31third/common';
import BigDecimal from 'js-big-decimal';
import { lastValueFrom } from 'rxjs';
import { APP_CONFIG } from 'app-config';
import { RebalancingRepository, RebalancingState } from '../repository';
import { RebalancingResponseDto } from '../dto';
import { RebalancingFactory } from '../factory';
import { GenericHttpMessage } from 'common';
import { Rebalancing } from '../model';
import { RebalancingStep } from '../component';

@Injectable({
  providedIn: 'root',
})
export class RebalancingExpiredQuotesService {
  private refreshQuotesTimeout: NodeJS.Timeout;

  constructor(
    @Inject(APP_CONFIG) private appConfig: any,
    private rebalancingRepository: RebalancingRepository,
    private httpClient: HttpClient,
  ) {
    this.subscribeToRebalancing();
  }

  private getBaseUrl(): string {
    return `${this.appConfig.tradingApiBaseUrl}/rebalancing`;
  }

  private get rebalancingState(): RebalancingState {
    return this.rebalancingRepository.store.getValue();
  }

  private subscribeToRebalancing(): void {
    this.rebalancingRepository.rebalancing$.subscribe(rebalancing => {
      this.clearRefresh();
      if (rebalancing && !rebalancing.isExpired()) {
        this.setRefreshTimeout(
          rebalancing,
          rebalancing.getTimeToSuggestedRefresh(),
        );
      }
    });
  }

  public clearRefresh(): void {
    if (this.refreshQuotesTimeout) {
      clearTimeout(this.refreshQuotesTimeout);
    }
  }

  private setRefreshTimeout(rebalancing: Rebalancing, timeoutMs: number): void {
    this.refreshQuotesTimeout = setTimeout(() => {
      if (
        this.rebalancingState.step > RebalancingStep.CALCULATE &&
        this.rebalancingState.step < RebalancingStep.EXPIRED
      ) {
        void this.refreshExpiredQuotes(rebalancing);
      }

      // if the user cancels the execution we should do another refresh
      if (this.rebalancingState.step === RebalancingStep.IN_PROGRESS) {
        this.setRefreshTimeout(rebalancing, 1_000);
      }
    }, timeoutMs);
  }

  private async refreshExpiredQuotes(rebalancing: Rebalancing): Promise<void> {
    this.rebalancingRepository.setStep(RebalancingStep.REFETCH_QUOTES);

    let params = new HttpParams();
    params = params.set('rebalancingId', rebalancing.id);

    try {
      const rebalancingResponseDto = await lastValueFrom(
        this.httpClient.get<RebalancingResponseDto>(
          `${this.getBaseUrl()}/refresh-expired-quotes`,
          {
            params,
          },
        ),
      );

      if (
        this.rebalancingRepository.store.getValue().step <
          RebalancingStep.REFETCH_QUOTES ||
        this.rebalancingRepository.store.getValue().step >
          RebalancingStep.APPROVAL_OK
      ) {
        return Promise.resolve();
      }

      const newRebalancing = RebalancingFactory.updateFromDto(
        new Rebalancing(),
        rebalancingResponseDto,
      );

      this.markPriceChanges(rebalancing, newRebalancing);
      this.rebalancingRepository.setRebalancing(newRebalancing);
    } catch (e: any) {
      if (e.error?.message && e.error?.message.length) {
        e.error.message.forEach((error: GenericHttpMessage) => {
          if (error.code === UNPROCESSABLE_REBALANCING_ERROR_CODE) {
            const rebalancing = this.rebalancingState.rebalancing;
            if (rebalancing) {
              rebalancing.expirationTimestamp = new Date();
              this.rebalancingRepository.setRebalancing(rebalancing);
            }
          }
        });
      }
      throw e;
    }
  }

  private markPriceChanges(
    oldRebalancing: Rebalancing,
    newRebalancing: Rebalancing,
  ): void {
    newRebalancing.trades.forEach((newTrade, index) => {
      const oldTrade = oldRebalancing.trades[index];
      const priceChangePercentage =
        1 -
        +new BigDecimal(oldTrade.price)
          .divide(new BigDecimal(newTrade.price), 10)
          .getValue();

      if (priceChangePercentage > 0.01) {
        newTrade.priceChange = true;
        this.rebalancingRepository.setPriceChangeWarning(true);
      }
    });
  }
}
